export type HealthTrendPoint = {
  year: number
  value: number
}

export type HealthTrend = {
  id: string
  label: string
  shortLabel: string
  unit: string
  indicatorCode: string
  direction: 'higher-is-generally-better' | 'lower-is-generally-better'
  context: string
  caveat: string
  sourceLabel: string
  sourceUrl: string
  color: string
  points: HealthTrendPoint[]
}

export const healthTrends: HealthTrend[] = [
  {
    id: 'life-expectancy',
    label: 'Global life expectancy at birth',
    shortLabel: 'Life expectancy',
    unit: 'years',
    indicatorCode: 'SP.DYN.LE00.IN',
    direction: 'higher-is-generally-better',
    context: 'The global estimate rose over the long term, with a visible pandemic-era setback before recovering above the 2019 level by 2023.',
    caveat: 'A world aggregate can hide large differences between countries, sexes and communities. Life expectancy is a population estimate, not a prediction for one person.',
    sourceLabel: 'World Bank Open Data',
    sourceUrl: 'https://data.worldbank.org/indicator/SP.DYN.LE00.IN?locations=1W',
    color: '#0e615b',
    points: [
      { year: 2000, value: 67.6504778738489 },
      { year: 2005, value: 69.111445965915 },
      { year: 2010, value: 70.6826307784169 },
      { year: 2015, value: 71.9656376386893 },
      { year: 2019, value: 72.869911203356 },
      { year: 2020, value: 72.1832040176208 },
      { year: 2021, value: 71.2160780260296 },
      { year: 2022, value: 72.9689585478052 },
      { year: 2023, value: 73.3302230276374 },
      { year: 2024, value: 73.4818184164654 },
    ],
  },
  {
    id: 'under-five-mortality',
    label: 'Global under-five mortality rate',
    shortLabel: 'Under-five mortality',
    unit: 'deaths per 1,000 live births',
    indicatorCode: 'SH.DYN.MORT',
    direction: 'lower-is-generally-better',
    context: 'The estimated global rate fell substantially from 2000 to 2024, although progress was uneven and the world total briefly worsened around 2021–2022.',
    caveat: 'These are modelled population estimates. National and local risks vary, and later releases may revise earlier years as source data improve.',
    sourceLabel: 'World Bank · UN IGME',
    sourceUrl: 'https://data.worldbank.org/indicator/SH.DYN.MORT?locations=1W',
    color: '#ff826f',
    points: [
      { year: 2000, value: 76.7 },
      { year: 2005, value: 62.3 },
      { year: 2010, value: 50.6 },
      { year: 2015, value: 42.9 },
      { year: 2019, value: 39.6 },
      { year: 2020, value: 39.2 },
      { year: 2021, value: 39.6 },
      { year: 2022, value: 40 },
      { year: 2023, value: 38.3 },
      { year: 2024, value: 37.4 },
    ],
  },
  {
    id: 'dpt-immunisation',
    label: 'Global DPT immunisation coverage',
    shortLabel: 'DPT immunisation',
    unit: '% of children ages 12–23 months',
    indicatorCode: 'SH.IMM.IDPT',
    direction: 'higher-is-generally-better',
    context: 'Coverage improved over the long term, fell during the pandemic years, and partially recovered by 2024 without yet returning to the 2019 estimate.',
    caveat: 'Coverage estimates describe populations, not an individual child’s vaccination status. Recommended vaccines and schedules differ by country.',
    sourceLabel: 'World Bank · WHO/UNICEF',
    sourceUrl: 'https://data.worldbank.org/indicator/SH.IMM.IDPT?locations=1W',
    color: '#6d8f2d',
    points: [
      { year: 2000, value: 72.3007318349417 },
      { year: 2005, value: 77.2658510295097 },
      { year: 2010, value: 83.6605290817133 },
      { year: 2015, value: 84.8338185916175 },
      { year: 2019, value: 86.7603989404926 },
      { year: 2020, value: 83.5467575655038 },
      { year: 2021, value: 82.0717055937382 },
      { year: 2022, value: 85.079535715363 },
      { year: 2023, value: 84.4904980090815 },
      { year: 2024, value: 84.7805894211456 },
    ],
  },
]
